import AppCard from '../../ui/AppCard';
import React, {useState} from 'react';
import { Dialog } from 'primereact/dialog';
import DonutChart, { getStatusColor } from '../../common/Charts/Donut'
import {useMountEffect} from 'primereact/hooks';
import StorageHelper from '../../../utils/storage-helper';


const EmployeeDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState([]);
    const [showAllProducts, setShowAllProducts] = useState(false);

    useMountEffect(() => {
        fetch(process.env.REACT_APP_DOVESAPI + 'report/dashboard', {
            headers: {
                Authorization: 'Bearer ' + StorageHelper.get('token'),
                'Content-Type': 'application/json'
            }
        })
            .then(response => response.json())
            .then(data => {
                setLoading(false)
                if (data.status === 'SUCCESS') {
                    setDashboardData(data.responseBody);
                } else {
                    console.error('Failed to fetch policy history:', data.message);
                }
            })
            .catch(error => {
                setLoading(false)
                console.error('Error fetching policy history:', error);
            });
    }, []);
    const sortedProductCategories = dashboardData.getApplicationsByProductCategory 
        ? [...dashboardData.getApplicationsByProductCategory].sort((a, b) => Number.parseInt(b.count) - Number.parseInt(a.count))
        : [];
        
    const limitedProductCategories = sortedProductCategories.slice(0, 5);
    const extraCount = sortedProductCategories.length - 5;

    return (
        <div className="content-body">
            <div className="w-full  p-4 md:p-2 md:px-6 mb-12">

                {loading ? (
                    <AppCard bodyClassName="p-16 flex items-center justify-center">
                        <span className="loading loading-spinner loading-lg text-primary"></span>
                    </AppCard>
                ) : (
                    <div className="flex flex-col gap-5">

                        {/* Stat Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                            {/* Total Apps */}
                            <AppCard className="py-6" bodyClassName="p-0 flex flex-col items-center justify-center text-center gap-1">
                                <div className="text-base-content/80 font-semibold text-sm">Total Applications</div>
                                <div className="text-5xl font-light text-primary">{dashboardData.applicationTotalRequests}</div>
                                <div className="text-xs text-base-content/40 font-semibold mt-1">Lifetime</div>
                            </AppCard>

                            {/* Completed Apps */}
                            <AppCard className="py-6" bodyClassName="p-0 flex flex-col items-center justify-center text-center gap-1">
                                <div className="text-base-content/80 font-semibold text-sm">Completed Applications</div>
                                <div className="text-5xl font-light text-primary">{dashboardData.getCompletedApplicationsTotal}</div>
                                <div className="text-xs text-base-content/40 font-semibold mt-1">Lifetime</div>
                            </AppCard>

                            {/* Incomplete Apps */}
                            <AppCard className="py-6" bodyClassName="p-0 flex flex-col items-center justify-center text-center gap-1">
                                <div className="text-base-content/80 font-semibold text-sm">Incomplete Applications</div>
                                <div className="text-5xl font-light text-error">{dashboardData.getIncompleteApplicationsTotal}</div>
                                <div className="text-xs text-base-content/40 font-semibold mt-1">Lifetime</div>
                            </AppCard>

                        </div>

                        {/* Detailed Data */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                            {/* Table Card */}
                            <AppCard 
                                className="flex flex-col h-full"
                                bodyClassName="p-0"
                                header={
                                    <div className="px-6 py-6 border-b border-base-200">
                                        <h4 className="font-bold text-base-content text-[15px]">Policy Applications (Grouped By Product Category)</h4>
                                    </div>
                                }
                            >
                                <div className="overflow-x-auto p-4">
                                    <table className="table w-full border-separate border-spacing-y-2">
                                        <thead>
                                        <tr className="text-base-content/60">
                                            <th className="font-bold text-xs">Product Name</th>
                                            <th className="font-bold text-xs text-right">Applications</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {limitedProductCategories.map((data) => (
                                            <tr key={data.product} className="bg-base-50/50 hover:bg-base-100 transition-colors">
                                                <td className="rounded-l-lg py-4">
                                                    <span className="font-semibold text-xs text-base-content uppercase">{data.product}</span>
                                                </td>
                                                <td className="text-right rounded-r-lg py-4">
                                                    <div className="inline-flex items-center gap-3">
                                                        <i className="pi pi-arrow-up text-[10px] font-bold text-primary"></i>
                                                        <span className="font-bold text-sm text-base-content">{data.count}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                    
                                    {extraCount > 0 && (
                                        <div className="flex justify-center mt-4">
                                            <button 
                                                className="btn btn-outline btn-sm rounded-full px-6"
                                                onClick={() => setShowAllProducts(true)}
                                            >
                                                + {extraCount} more... View All
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </AppCard>

                            {/* Chart Card */}
                            <AppCard 
                                className="flex flex-col h-full"
                                bodyClassName="p-6 flex flex-col md:flex-row items-center justify-center gap-10"
                                header={
                                    <div className="px-6 py-6 border-b border-base-200">
                                        <h4 className="font-bold text-base-content text-[15px]">Policy applications (Grouped By Progress / Status)</h4>
                                    </div>
                                }
                            >
                                <div className="flex justify-center w-full md:w-1/2 min-h-[200px]">
                                    {dashboardData.getApplicationsByStatus ?
                                        <DonutChart getApplicationsByStatus={dashboardData.getApplicationsByStatus}/>
                                        : <div className="w-full h-full flex items-center justify-center text-base-content/40 italic">Loading chart...</div>}
                                </div>

                                <div className="flex flex-col gap-6 w-full md:w-1/2">
                                    {dashboardData.getApplicationsByStatus?.map((data, index) => (
                                        <div key={data.status} className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getStatusColor(data.status, index) }}></div>
                                                <span className="font-semibold text-xs text-base-content/80">{data.status}</span>
                                            </div>
                                            <div className="font-semibold text-sm text-base-content/90">
                                                {data.count}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </AppCard>

                        </div>
                    </div>
                )}
            </div>


            <Dialog header="All Product Categories" visible={showAllProducts} style={{ width: '50vw' }} onHide={() => setShowAllProducts(false)}>
                <div className="overflow-y-auto max-h-[60vh] p-4">
                    <table className="table w-full border-separate border-spacing-y-2">
                        <thead>
                            <tr className="text-base-content/60">
                                <th className="font-bold text-xs">Product Name</th>
                                <th className="font-bold text-xs text-right">Applications</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedProductCategories.map((data) => (
                                <tr key={data.product} className="bg-base-50/50 hover:bg-base-100 transition-colors">
                                    <td className="rounded-l-lg py-4">
                                        <span className="font-semibold text-xs text-base-content uppercase">{data.product}</span>
                                    </td>
                                    <td className="text-right rounded-r-lg py-4">
                                        <div className="inline-flex items-center gap-3">
                                            <i className="pi pi-arrow-up text-[10px] font-bold text-primary"></i>
                                            <span className="font-bold text-sm text-base-content">{data.count}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Dialog>
        </div>
    );
};

export default EmployeeDashboard;// trigger recompile
